import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { Button, Card, Badge } from '../components/ui';
import { isValidImageUrl } from '../utils/imageUtils';

interface Product {
  id?: number;
  name: string;
  description: string;
  price: number;
  media: string[];
  status: string;
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/products')
      .then(res => {
        const p = res.data.find((x: any) => String(x.id) === String(id));
        setProduct(p || null);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAdd = () => {
    if (!product || adding) return;
    setAdding(true);
    add({ product_id: product.id || '', name: product.name, price: Number(product.price), quantity: 1 });
    setTimeout(() => setAdding(false), 400);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-12 w-12 border-2 border-rs-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-300 mb-4">Producto no encontrado</h1>
        <Link to="/">
          <Button variant="outline">Volver al catálogo</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-rs-accent mb-8 transition-colors">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Volver al catálogo
      </Link>

      <Card padding="none" className="overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          <div className="aspect-video md:aspect-square bg-rs-surfaceLight flex items-center justify-center">
            {product.media && product.media[0] && isValidImageUrl(product.media[0]) ? (
              <img src={product.media[0]} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="relative w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#1c0a0a] via-[#141414] to-[#0c0c0c]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(225,6,0,0.18),transparent_62%)]" />
                <span className="relative font-display text-7xl tracking-widest text-white/25">
                  {product.name.charAt(0).toUpperCase()}
                </span>
                <span className="relative mt-2 text-xs uppercase tracking-[0.28em] text-gray-400">
                  Sin imagen
                </span>
              </div>
            )}
          </div>
          <div className="p-8 flex flex-col justify-between">
            <div>
              <Badge variant={product.status === 'activo' ? 'success' : 'default'} className="mb-4">
                {product.status}
              </Badge>
              <h1 className="text-3xl font-display font-bold text-gray-100 mb-4">{product.name}</h1>
              <p className="text-gray-400 mb-6 leading-relaxed">{product.description}</p>
              {product.media && product.media.length > 0 && (
                <div className="space-y-2 mb-6">
                  <p className="text-sm text-gray-500">Archivos / Media:</p>
                  {product.media.map((m, i) => (
                    isValidImageUrl(m) ? (
                      <a key={i} href={m} target="_blank" rel="noreferrer" className="block text-rs-accent hover:underline truncate">
                        Ver media {i + 1}
                      </a>
                    ) : (
                      <span key={i} className="block text-gray-500 truncate">Media {i + 1}: {m}</span>
                    )
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 pt-6 border-t border-rs-border">
              <span className="text-3xl font-display font-bold text-rs-accent">${product.price}</span>
              <Button
                size="lg"
                onClick={handleAdd}
                disabled={adding}
                loading={adding}
                leftIcon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                }
              >
                Agregar al carrito
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
